import { PlanReturnsI, PlanReturnsItemI } from '../api/plan-returns/getPlanReturns';
import { MonthlyRate, RateStage } from './monthlyProjection';

/**
 * Rentabilidad de un plan tal y como la decide comparsa (`GET /plan-returns`), lista para la
 * gráfica, la tabla, el consejo y la retrospectiva del traspaso. Fuente única: ninguna pieza
 * elige periodo ni suaviza por su cuenta.
 */
export interface PlanReturnView {
	/** Anualizada y NETA de comisiones, en %. Para mostrar. */
	annualPct: number;
	/** Mensual, sin redondear: la que usa la curva mes a mes. */
	monthlyPct: number;
	periodYears: number;
	/** Fechas del dato (ISO). */
	period: { start: string; end: string } | null;
	/** Con 1 o 3 años: aviso de histórico corto. */
	shortHistory: boolean;
	/** «Comisión de gestión», en %; `null` si comparsa no la tiene. */
	managementFeePct: number | null;
	/**
	 * Ciclo de vida con edad: los tramos de comparsa para la curva (`annualPct` es el de hoy).
	 * `null` (o ausente) en los demás planes, o sin edad (entonces la mezcla 50/50 de siempre).
	 */
	schedule?: RateStage[] | null;
}

export interface TransferReturns {
	/** `null` si comparsa no tiene datos del plan de Arca. */
	arca: PlanReturnView | null;
	/** `null` si el plan del usuario no tiene datos: se oculta su curva con un aviso. */
	origin: PlanReturnView | null;
	/** Los dos con el mismo periodo. */
	samePeriod: boolean;
}

function toView(item: PlanReturnsItemI | null | undefined): PlanReturnView | null {
	if (
		!item ||
		item.noData ||
		item.annualReturnPct === null ||
		item.monthlyReturnPct === null ||
		item.periodYears === null
	) {
		return null;
	}
	return {
		annualPct: item.annualReturnPct,
		monthlyPct: item.monthlyReturnPct,
		periodYears: item.periodYears,
		period: item.period,
		shortHistory: item.shortHistoryWarning,
		managementFeePct: item.managementFeePct,
		// Un calendario vacío sería un error de comparsa: como no tenerlo, nunca romper la curva.
		schedule: item.lifecycle?.schedule?.length
			? item.lifecycle.schedule.map(({ fromYear, toYear, monthlyReturnPct }) => ({
					fromYear,
					toYear,
					monthlyReturnPct
				}))
			: null
	};
}

type TranslateFn = (key: string) => string;

/**
 * Ciclo de vida cuya rentabilidad (la de hoy, la que se enseña) irá bajando: el último tramo rinde
 * menos que el primero. Si en el periodo los bonos rindieron más, sube y no se dice nada.
 */
export function dropsWithAge(view: PlanReturnView): boolean {
	const schedule = view.schedule ?? [];
	return (
		schedule.length > 1 &&
		schedule[schedule.length - 1].monthlyReturnPct < schedule[0].monthlyReturnPct
	);
}

/** La cifra suelta: «hoy 7,83%, baja con la edad» en un ciclo de vida que baja; si no, tal cual. */
export function todayPct(translate: TranslateFn, view: PlanReturnView, pct: string): string {
	if (!dropsWithAge(view)) return pct;
	return (translate('returns.lifecycle.today') || 'hoy {pct}, baja con la edad').replace(
		'{pct}',
		pct
	);
}

/** Lo que se añade a una leyenda: « · baja con la edad», o nada. */
export function ageNote(translate: TranslateFn, view: PlanReturnView): string {
	if (!dropsWithAge(view)) return '';
	return ` · ${translate('returns.lifecycle.dropsWithAge') || 'baja con la edad'}`;
}

/** Lo que se proyecta hacia delante: los tramos del ciclo de vida o, si no, la tasa mensual. */
export function rateOf(view: PlanReturnView): MonthlyRate {
	return view.schedule ?? view.monthlyPct;
}

/** Traspaso: el (único) plan de Arca pedido frente al plan del usuario. */
export function toTransferReturns(response: PlanReturnsI): TransferReturns {
	return {
		arca: toView(response.arcaPlans[0]),
		origin: toView(response.originPlan),
		samePeriod: response.samePeriod
	};
}

/**
 * Plan nuevo y listas de planes (`/plan-returns/arca-plans`): la rentabilidad de cada plan de
 * Arca por su slug del catálogo (`ArcaPlan.catalogSlug`). `null` = sin datos: se dice así,
 * nunca se pone una tasa de respaldo.
 */
export function toArcaPlanReturnsBySlug(
	response: PlanReturnsI
): Record<string, PlanReturnView | null> {
	const bySlug: Record<string, PlanReturnView | null> = {};
	response.arcaPlans.forEach((item) => {
		if (item.catalogName) bySlug[item.catalogName] = toView(item);
	});
	return bySlug;
}

/** Con datos del plan de Arca, la comparativa se puede pintar (si no, ninguna cifra). */
export function hasArcaReturns(
	returns: TransferReturns | null | undefined
): returns is TransferReturns & { arca: PlanReturnView } {
	return !!returns?.arca;
}

type Translate = (key: string) => string;

/** «dic. 2025» con los meses traducidos (`months.N`); sin traducción, «12/2025». */
function monthYear(translate: Translate, iso: string): string {
	const [year, month] = iso.split('-');
	const name = translate(`months.${Number(month)}`);
	return name ? `${name.slice(0, 3).toLowerCase()}. ${year}` : `${month}/${year}`;
}

/** Fechas del dato para la leyenda: «dic. 2020 – dic. 2025». Vacío sin fechas. */
export function periodRangeLabel(
	translate: Translate,
	period: { start: string; end: string } | null
): string {
	if (!period) return '';
	return `${monthYear(translate, period.start)} – ${monthYear(translate, period.end)}`;
}
